import type { ComponentProps, ReactNode } from 'react';
import {
  createContext,
  useContext,
  useMemo,
  Children,
  isValidElement,
  cloneElement,
  forwardRef,
} from 'react';
import { cn } from './helpers';

interface ShowSpecificNumberContextValue {
  maxItems: number;
  totalItems: number;
  remainingCount: number;
}

const ShowSpecificNumberContext =
  createContext<ShowSpecificNumberContextValue | null>(null);

const useShowSpecificNumberContext = () => {
  const context = useContext(ShowSpecificNumberContext);
  if (!context) {
    throw new Error(
      'ShowSpecificNumber components must be used within a ShowSpecificNumberContext provider'
    );
  }
  return context;
};

interface ShowSpecificNumberItemProps extends ComponentProps<'div'> {
  children?: ReactNode;
}

function ShowSpecificNumberItem({
  children,
  className,
  ...props
}: ShowSpecificNumberItemProps) {
  return (
    <div
      className={cn(className)}
      {...props}
    >
      {children}
    </div>
  );
}

ShowSpecificNumberItem.displayName = 'ShowSpecificNumberItem';

interface ShowSpecificNumberExtraProps extends Omit<
  ComponentProps<'div'>,
  'children'
> {
  children?: ReactNode | ((remainingCount: number) => ReactNode);
}

const ShowSpecificNumberExtra = forwardRef<
  HTMLDivElement,
  ShowSpecificNumberExtraProps
>(function ShowSpecificNumberExtra({ children, className, ...props }, ref) {
  const { remainingCount } = useShowSpecificNumberContext();

  if (remainingCount <= 0) {
    return null;
  }

  return (
    <div
      ref={ref}
      className={cn('flex items-center justify-center', className)}
      {...props}
    >
      {typeof children === 'function'
        ? children(remainingCount)
        : children || `+${remainingCount}`}
    </div>
  );
});

ShowSpecificNumberExtra.displayName = 'ShowSpecificNumberExtra';

interface ShowSpecificNumberContextProps {
  maxItems: number;
  children: ReactNode;
}

function ShowSpecificNumberContextProvider({
  maxItems,
  children,
}: ShowSpecificNumberContextProps) {
  // First pass: count only ShowSpecificNumberItem components
  const totalItems = useMemo(() => {
    let count = 0;

    const countItems = (node: ReactNode): void => {
      if (isValidElement(node)) {
        const componentType = node.type;

        // Check if it's directly ShowSpecificNumberItem
        if (componentType === ShowSpecificNumberItem) {
          count++;
          return;
        }

        // Check displayName for wrapped components (like forwardRef)
        if (
          typeof componentType === 'function' ||
          typeof componentType === 'object'
        ) {
          const displayName = (componentType as { displayName?: string })
            ?.displayName;
          if (displayName === 'ShowSpecificNumberItem') {
            count++;
            return;
          }
        }

        // Always recursively check children to find nested items
        if (node.props?.children) {
          Children.forEach(node.props.children, countItems);
        }
      }
    };

    Children.forEach(children, countItems);
    return count;
  }, [children]);

  const remainingCount = Math.max(0, totalItems - maxItems);

  const contextValue = useMemo<ShowSpecificNumberContextValue>(
    () => ({
      maxItems,
      totalItems,
      remainingCount,
    }),
    [maxItems, totalItems, remainingCount]
  );

  // Second pass: render children, filtering items beyond maxItems and injecting extra
  const renderChildren = (): ReactNode => {
    let itemIndex = 0;
    let extraRendered = false;

    const processNode = (node: ReactNode): ReactNode => {
      if (isValidElement(node)) {
        const componentType = node.type;

        // Check if it's directly ShowSpecificNumberItem
        const isDirectItem = componentType === ShowSpecificNumberItem;
        const isDirectExtra = componentType === ShowSpecificNumberExtra;

        // Check displayName for wrapped components (like forwardRef)
        let displayName: string | undefined;
        if (
          (typeof componentType === 'function' ||
            typeof componentType === 'object') &&
          !isDirectItem &&
          !isDirectExtra
        ) {
          displayName = (componentType as { displayName?: string })
            ?.displayName;
        }

        const isItem = isDirectItem || displayName === 'ShowSpecificNumberItem';
        const isExtra =
          isDirectExtra || displayName === 'ShowSpecificNumberExtra';

        if (isItem) {
          if (itemIndex < maxItems) {
            itemIndex++;
            return node;
          }
          return null; // Hide items beyond maxItems
        } else if (isExtra) {
          if (remainingCount > 0 && !extraRendered) {
            extraRendered = true;
            return node;
          }
          return null;
        } else if (node.props?.children) {
          // Recursively process children of container elements
          const mapped = Children.map(node.props.children, processNode);
          const processedChildren = mapped
            ? mapped.filter((child) => child != null)
            : [];
          return cloneElement(node, undefined, ...processedChildren);
        }
      }
      return node;
    };

    const result = Children.map(children, processNode);
    return result ? result.filter((child) => child != null) : [];
  };

  return (
    <ShowSpecificNumberContext.Provider value={contextValue}>
      {renderChildren()}
    </ShowSpecificNumberContext.Provider>
  );
}

ShowSpecificNumberContextProvider.displayName = 'ShowSpecificNumberContext';

export {
  ShowSpecificNumberContextProvider as ShowSpecificNumberContext,
  ShowSpecificNumberItem,
  ShowSpecificNumberExtra,
};
